<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import {
  getDepreciationProfiles, createDepreciationProfile,
  updateDepreciationProfile, deleteDepreciationProfile,
  getTenantSettings, updateTenantSettings,
} from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import MdiPencil from '~icons/mdi/pencil'
import MdiDeleteOutline from '~icons/mdi/delete-outline'
import MdiPlus from '~icons/mdi/plus'
import MdiTrendingDown from '~icons/mdi/trending-down'
import MdiInformationOutline from '~icons/mdi/information-outline'
import MdiStar from '~icons/mdi/star'
import MdiStarOutline from '~icons/mdi/star-outline'
import MdiChevronRight from '~icons/mdi/chevron-right'

const { t } = useI18n()
const toast = useToast()

// ─── Constants ────────────────────────────────────────────────────────────────

const METHOD_OPTIONS = computed(() => [
  { value: 'STRAIGHT_LINE',     label: t('depreciation.methods.STRAIGHT_LINE') },
  { value: 'DECLINING_BALANCE', label: t('depreciation.methods.DECLINING_BALANCE') },
  { value: 'INSURANCE_STEP',    label: t('depreciation.methods.INSURANCE_STEP') },
])
const METHOD_LABELS = computed(() => Object.fromEntries(METHOD_OPTIONS.value.map(o => [o.value, o.label])))

function blankForm() {
  return {
    name: '',
    method: 'STRAIGHT_LINE',
    annual_rate: '',
    salvage_value_percentage: '0',
    grace_period_years: '0',
  }
}

// ─── State ────────────────────────────────────────────────────────────────────

const profiles       = ref([])
const loading        = ref(false)
const loadError      = ref(null)
const tenantDefault  = ref(null)
const savingDefault  = ref(false)
const availabilitySettings = ref({ include_drafts: false, buffer_before_minutes: 0, buffer_after_minutes: 0 })
const savingAvailability = ref(false)

const editingId      = ref(null)   // null | 'new' | profile.id
const editForm       = ref(blankForm())
const saving         = ref(false)
const saveError      = ref(null)

const showDeleteConfirm = ref(false)
const deleteTarget      = ref(null)
const deleting          = ref(false)

// ─── Load ─────────────────────────────────────────────────────────────────────

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    const [profileRes, settingsRes] = await Promise.all([
      getDepreciationProfiles(),
      getTenantSettings(),
    ])
    profiles.value      = (profileRes.results ?? profileRes ?? []).sort((a, b) => a.name.localeCompare(b.name))
    tenantDefault.value = settingsRes.default_depreciation_profile ?? null
    availabilitySettings.value = {
      include_drafts: settingsRes.availability?.include_drafts ?? false,
      buffer_before_minutes: settingsRes.availability?.buffer_before_minutes ?? 0,
      buffer_after_minutes: settingsRes.availability?.buffer_after_minutes ?? 0,
    }
  } catch (e) {
    loadError.value = e?.data?.detail ?? t('depreciation.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)

// ─── Tenant default ───────────────────────────────────────────────────────────

const defaultProfile = computed(() => profiles.value.find(p => p.id === tenantDefault.value) ?? null)

async function setDefault(profileId) {
  savingDefault.value = true
  try {
    const res = await updateTenantSettings({ default_depreciation_profile: profileId })
    tenantDefault.value = res.default_depreciation_profile ?? null
    toast.success(profileId ? t('depreciation.defaultUpdated') : t('depreciation.defaultCleared'))
  } catch {
    toast.error(t('depreciation.updateDefaultFailed'))
  } finally {
    savingDefault.value = false
  }
}

async function saveAvailabilitySettings() {
  savingAvailability.value = true
  try {
    const res = await updateTenantSettings({ availability: { ...availabilitySettings.value } })
    availabilitySettings.value = { ...res.availability }
    toast.success(t('depreciation.availabilitySaved'))
  } catch {
    toast.error(t('depreciation.availabilitySaveFailed'))
  } finally {
    savingAvailability.value = false
  }
}

// ─── Edit / Create ────────────────────────────────────────────────────────────

function startEdit(profile) {
  editingId.value = profile.id
  editForm.value  = {
    name:                     profile.name,
    method:                   profile.method,
    annual_rate:              (parseFloat(profile.annual_rate) * 100).toFixed(2),
    salvage_value_percentage: (parseFloat(profile.salvage_value_percentage) * 100).toFixed(2),
    grace_period_years:       String(profile.grace_period_years ?? 0),
  }
  saveError.value = null
}

function startNew() {
  editingId.value = 'new'
  editForm.value  = blankForm()
  saveError.value = null
}

function cancelEdit() {
  editingId.value = null
  saveError.value = null
}

const editFormValid = computed(() =>
  editForm.value.name.trim() !== '' &&
  editForm.value.method !== '' &&
  !isNaN(parseFloat(editForm.value.annual_rate)) &&
  parseFloat(editForm.value.annual_rate) > 0
)

async function saveProfile() {
  if (!editFormValid.value) return
  saving.value    = true
  saveError.value = null
  const payload = {
    name:                     editForm.value.name.trim(),
    method:                   editForm.value.method,
    annual_rate:              (parseFloat(editForm.value.annual_rate) / 100).toFixed(4),
    salvage_value_percentage: (parseFloat(editForm.value.salvage_value_percentage || 0) / 100).toFixed(4),
    grace_period_years:       parseInt(editForm.value.grace_period_years || 0),
  }
  try {
    if (editingId.value === 'new') {
      const created = await createDepreciationProfile(payload)
      profiles.value = [...profiles.value, created].sort((a, b) => a.name.localeCompare(b.name))
      toast.success(t('depreciation.profileCreated'))
    } else {
      const updated = await updateDepreciationProfile(editingId.value, payload)
      profiles.value = profiles.value.map(p => p.id === updated.id ? { ...p, ...updated } : p)
      toast.success(t('depreciation.profileUpdated'))
    }
    editingId.value = null
  } catch (e) {
    saveError.value = e?.data?.name?.[0] ?? e?.data?.detail ?? t('ui.saveFailed')
  } finally {
    saving.value = false
  }
}

// ─── Delete ───────────────────────────────────────────────────────────────────

function promptDelete(profile) {
  deleteTarget.value      = profile
  showDeleteConfirm.value = true
}

async function doDelete() {
  if (!deleteTarget.value) return
  deleting.value = true
  try {
    await deleteDepreciationProfile(deleteTarget.value.id)
    profiles.value = profiles.value.filter(p => p.id !== deleteTarget.value.id)
    if (tenantDefault.value === deleteTarget.value.id) tenantDefault.value = null
    toast.success(t('depreciation.deleted'))
    showDeleteConfirm.value = false
    deleteTarget.value      = null
  } catch (e) {
    toast.error(e?.data?.detail ?? t('depreciation.deleteFailedInUse'))
    showDeleteConfirm.value = false
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <div class="page-view">
    <BasePageHeader :title="$t('depreciation.title')" :show-back="false">
      <template #actions>
        <button class="btn primary small" @click="startNew">
          <MdiPlus class="w-4 h-4 mr-1" />{{ $t('depreciation.new') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- ── Error ────────────────────────────────────────────────── -->
    <div v-if="loadError" class="px-4 pt-4">
      <div class="flex items-center gap-2 text-sm text-red-400 bg-red-950/30 border border-red-900 rounded-xl px-4 py-3">
        {{ loadError }}
      </div>
    </div>

    <!-- ── Loading skeleton ──────────────────────────────────────── -->
    <div v-if="loading" class="px-4 pt-4 flex flex-col gap-3 animate-pulse">
      <div class="h-24 rounded-xl" style="background: var(--color-surface)" />
      <div class="h-14 rounded-xl" style="background: var(--color-surface)" />
      <div class="h-14 rounded-xl" style="background: var(--color-surface)" />
    </div>

    <template v-if="!loading">

      <!-- ── Tenant default card ────────────────────────────────── -->
      <div class="px-4 pt-4 pb-1">
        <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
          <div class="px-4 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
            <MdiStar class="w-4 h-4 text-amber-400 shrink-0" />
            <span class="section-label mb-0">{{ $t('depreciation.tenantDefaultTitle') }}</span>
          </div>
          <div class="px-4 py-4">
            <p class="text-xs mb-3" style="color: var(--color-muted)">
              {{ $t('depreciation.tenantDefaultHint') }}
            </p>
            <div v-if="defaultProfile" class="flex items-center gap-3">
              <div class="flex-1 bg-amber-950/20 border border-amber-900/40 rounded-lg px-3 py-2.5">
                <div class="text-sm font-semibold" style="color: var(--color-text)">{{ defaultProfile.name }}</div>
                <div class="text-xs mt-0.5" style="color: var(--color-muted)">
                  {{ METHOD_LABELS[defaultProfile.method] }} &middot;
                  {{ (parseFloat(defaultProfile.annual_rate) * 100).toFixed(1) }}{{ $t('ui.pA') }}
                </div>
              </div>
              <button class="btn ghost small" :disabled="savingDefault" @click="setDefault(null)">
                {{ $t('depreciation.removeDefault') }}
              </button>
            </div>
            <div
              v-else
              class="flex items-start gap-2 text-xs rounded-lg px-3 py-2.5 border"
              style="color: var(--color-muted); background: rgba(255,255,255,0.02); border-color: var(--color-border)"
            >
              <MdiInformationOutline class="w-4 h-4 shrink-0 mt-0.5 opacity-50" />
              <span>{{ $t('depreciation.noDefaultSet') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Booking availability settings ───────────────────────── -->
      <div class="px-4 pt-3 pb-1">
        <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
          <div class="px-4 py-3 border-b border-[var(--color-border)]">
            <span class="section-label mb-0">{{ $t('depreciation.availabilityTitle') }}</span>
          </div>
          <div class="px-4 py-4 space-y-4">
            <p class="text-xs text-[var(--color-muted)]">{{ $t('depreciation.availabilityHint') }}</p>
            <BaseFormField :label="$t('depreciation.includeDrafts')">
              <BaseCheckbox v-model="availabilitySettings.include_drafts" :label="$t('depreciation.includeDrafts')" />
            </BaseFormField>
            <div class="form-row">
              <BaseFormField :label="$t('depreciation.bufferBefore')">
                <BaseInput v-model.number="availabilitySettings.buffer_before_minutes" type="number" min="0" max="525600" step="1" />
              </BaseFormField>
              <BaseFormField :label="$t('depreciation.bufferAfter')">
                <BaseInput v-model.number="availabilitySettings.buffer_after_minutes" type="number" min="0" max="525600" step="1" />
              </BaseFormField>
            </div>
            <button class="btn primary small" :disabled="savingAvailability" @click="saveAvailabilitySettings">
              {{ savingAvailability ? $t('common.saving') : $t('common.save') }}
            </button>
          </div>
        </div>
      </div>

      <!-- ── New profile form ───────────────────────────────────── -->
      <div v-if="editingId === 'new'" class="px-4 pt-3">
        <div class="bg-[var(--color-surface)] border border-blue-800/60 rounded-xl overflow-hidden">
          <div class="px-4 py-3 border-b border-[var(--color-border)]">
            <span class="section-label mb-0">{{ $t('depreciation.newProfileHeader') }}</span>
          </div>
          <div class="px-4 py-4">
            <div class="form-row mb-3" style="grid-template-columns: 1fr 1fr">
              <BaseFormField :label="$t('depreciation.nameLabel')" style="grid-column: 1 / -1">
                <BaseInput v-model="editForm.name" type="text" :placeholder="$t('ui.eGStandard10')" />
              </BaseFormField>
              <BaseFormField :label="$t('depreciation.methodLabel')">
                <BaseSelect v-model="editForm.method">
                  <option v-for="opt in METHOD_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </BaseSelect>
              </BaseFormField>
              <BaseFormField :label="$t('depreciation.annualRate')">
                <BaseInput v-model="editForm.annual_rate" type="number" min="0.01" max="100" step="0.01" :placeholder="$t('ui.eG10')" />
              </BaseFormField>
              <BaseFormField :label="$t('depreciation.salvageValue')">
                <BaseInput v-model="editForm.salvage_value_percentage" type="number" min="0" max="100" step="0.01" placeholder="0" />
              </BaseFormField>
              <BaseFormField v-if="editForm.method === 'INSURANCE_STEP'" :label="$t('depreciation.gracePeriod')">
                <BaseInput v-model="editForm.grace_period_years" type="number" min="0" max="20" step="1" placeholder="0" />
              </BaseFormField>
            </div>
            <p v-if="saveError" class="text-sm text-red-400 mb-3">{{ saveError }}</p>
            <div class="flex gap-2">
              <button class="btn primary small" :disabled="!editFormValid || saving" @click="saveProfile">
                {{ saving ? $t('common.saving') : $t('depreciation.saveProfile') }}
              </button>
              <button class="btn ghost small" :disabled="saving" @click="cancelEdit">{{ $t('common.cancel') }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Empty state ────────────────────────────────────────── -->
      <div v-if="profiles.length === 0 && editingId !== 'new'" class="state-empty">
        {{ $t('depreciation.noProfilesYet') }}<br />{{ $t('depreciation.noProfilesHint') }}
      </div>

      <!-- ── Profile list ───────────────────────────────────────── -->
      <div class="list-stack">
        <div
          v-for="profile in profiles"
          :key="profile.id"
          class="data-card"
          :class="{ 'border-blue-800/60': editingId === profile.id }"
        >
          <!-- View mode -->
          <template v-if="editingId !== profile.id">
            <MdiTrendingDown class="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div class="data-card__main">
              <div class="data-card__name flex items-center gap-2 flex-wrap">
                {{ profile.name }}
                <span
                  v-if="profile.id === tenantDefault"
                  class="badge badge-warning badge-pill text-[0.65rem]"
                >
                  <MdiStar class="w-2.5 h-2.5 mr-0.5" />{{ $t('depreciation.tenantDefaultBadge') }}
                </span>
              </div>
              <div class="data-card__meta">
                <span class="badge badge-muted">{{ METHOD_LABELS[profile.method] ?? profile.method }}</span>
                <span class="badge badge-muted">{{ (parseFloat(profile.annual_rate) * 100).toFixed(1) }}{{ $t('ui.pA') }}</span>
                <span v-if="parseFloat(profile.salvage_value_percentage) > 0" class="badge badge-muted">
                  {{ $t('depreciation.salvagePct', { pct: (parseFloat(profile.salvage_value_percentage) * 100).toFixed(1) }) }}
                </span>
                <span v-if="profile.method === 'INSURANCE_STEP' && parseInt(profile.grace_period_years) > 0" class="badge badge-muted">
                  {{ $t('depreciation.graceYr', { n: profile.grace_period_years }) }}
                </span>
              </div>
              <div v-if="profile.item_count > 0 || profile.instance_count > 0" class="data-card__meta mt-0.5">
                <span v-if="profile.item_count > 0" class="badge badge-accent">{{ profile.item_count }} {{ $t('ui.itemTypeLowercase') }}{{ profile.item_count === 1 ? '' : 's' }}</span>
                <span v-if="profile.instance_count > 0" class="badge badge-accent">{{ profile.instance_count }} {{ $t('ui.instance') }}{{ profile.instance_count === 1 ? '' : 's' }}</span>
              </div>
            </div>
            <div class="data-card__actions">
              <button
                class="btn-icon"
                :class="profile.id === tenantDefault ? 'text-amber-400' : ''"
                :title="profile.id === tenantDefault ? $t('depreciation.currentDefault') : $t('depreciation.setAsDefault')"
                :disabled="savingDefault"
                @click="setDefault(profile.id === tenantDefault ? null : profile.id)"
              >
                <component :is="profile.id === tenantDefault ? MdiStar : MdiStarOutline" class="w-4 h-4" />
              </button>
              <button class="btn-icon" :title="$t('depreciation.editProfileHeader')" @click="startEdit(profile)">
                <MdiPencil class="w-4 h-4" />
              </button>
              <button class="btn-icon danger" :title="$t('depreciation.deleteConfirmTitle')" @click="promptDelete(profile)">
                <MdiDeleteOutline class="w-4 h-4" />
              </button>
            </div>
          </template>

          <!-- Edit mode (inline) -->
          <template v-else>
            <div class="w-full">
              <div class="section-label mb-3">{{ $t('depreciation.editProfileHeader') }}</div>
              <div class="form-row mb-3" style="grid-template-columns: 1fr 1fr">
                <BaseFormField :label="$t('depreciation.nameLabel')" style="grid-column: 1 / -1">
                  <BaseInput v-model="editForm.name" type="text" :placeholder="$t('ui.eGStandard10')" />
                </BaseFormField>
                <BaseFormField :label="$t('depreciation.methodLabel')">
                  <BaseSelect v-model="editForm.method">
                    <option v-for="opt in METHOD_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </BaseSelect>
                </BaseFormField>
                <BaseFormField :label="$t('depreciation.annualRate')">
                  <BaseInput v-model="editForm.annual_rate" type="number" min="0.01" max="100" step="0.01" :placeholder="$t('ui.eG10')" />
                </BaseFormField>
                <BaseFormField :label="$t('depreciation.salvageValue')">
                  <BaseInput v-model="editForm.salvage_value_percentage" type="number" min="0" max="100" step="0.01" placeholder="0" />
                </BaseFormField>
                <BaseFormField v-if="editForm.method === 'INSURANCE_STEP'" :label="$t('depreciation.gracePeriod')">
                  <BaseInput v-model="editForm.grace_period_years" type="number" min="0" max="20" step="1" placeholder="0" />
                </BaseFormField>
              </div>
              <p v-if="saveError" class="text-sm text-red-400 mb-3">{{ saveError }}</p>
              <div class="flex gap-2">
                <button class="btn primary small" :disabled="!editFormValid || saving" @click="saveProfile">
                  {{ saving ? $t('common.saving') : $t('common.save') }}
                </button>
                <button class="btn ghost small" :disabled="saving" @click="cancelEdit">{{ $t('common.cancel') }}</button>
              </div>
            </div>
          </template>
        </div>
      </div>

    </template>

    <!-- ── Delete confirmation modal ─────────────────────────────── -->
    <BaseConfirmModal
      :show="showDeleteConfirm"
      :title="$t('depreciation.deleteConfirmTitle')"
      :body="$t('depreciation.deleteConfirmBody', { name: deleteTarget?.name ?? '' })"
      :confirm-label="$t('common.delete')"
      :loading="deleting"
      @confirm="doDelete"
      @cancel="showDeleteConfirm = false"
    />
  </div>
</template>
